import Button from '@components/Button';
import FormHeader from '@components/FormHeader';
import Icons from '@components/Icons';
import Popover from '@components/Popover';
import TypographyRich from '@components/TypographyRich';
import Text from '@components/Text';
import Colors from '@constants/Colors';
import { Alert, Checkbox, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import React, { useEffect, useMemo } from 'react';

type PremiumType = 'dismissalGuarantee' | 'resignationGuarantee' | 'noGuarantee';
export type PremiumOption = {
    type?: PremiumType;
    info?: string;
    name: string;
    description?: string;
    warn?: string;
    price?: {
        amount?: number;
        percentage?: number;
        description: string;
    };
};

export const permiumOptions: PremiumOption[] = [
    {
        type: 'dismissalGuarantee',
        name: "Garantie fin de période d'essai du fait du salarié",
        description: `Valable les 60 premiers jours de la période d'essai`,
        warn:
            "En choisissant cette option, vous devez savoir que la  rupture de la période d'essai du fait de l'employeur ne sera pas couverte.",
        info:
            "Cette garantie vous protège contre un départ volontaire du salarié recruté pendant la période d'essai initiale de 2 mois. \n\n En cas de démission du salarié pendant la période initiale, Upercut s'engage à rechercher un nouveau candidat pour la même fonction sans honoraires supplémentaires sur une durée de trois mois.\n\n Compte tenu des tensions sur le marché du travail, la présentation de candidat n'est pas une obligation et n'est pas opposable.",
        price: {
            percentage: 1,
            description: 'Du salaire brut annuel',
        },
    },
    {
        type: 'resignationGuarantee',
        name: "Garantie fin de période d'essai du fait de l'employeur",
        description: `Valable les 45 premiers jours de la période d'essai`,
        warn:
            "En choisissant cette option, vous devez savoir que la  rupture de la période d'essai du fait du salarié ne sera pas couverte.",
        info:
            "Cette garantie vous protège dans le cas ou vous mettriez fin à la période d'essai du salarié recruté au cours des 45 premiers jours du contrat.\n\n En cas de licenciement du salarié au cours des 45 premiers jours du contrat, Upercut s'engage à rechercher un nouveau candidat pour la même fonction sans honoraires supplémentaires sur une durée de trois mois.\n\n Compte tenu des tensions sur le marché du travail, la présentation de candidat n'est pas une obligation et n'est pas opposable.",
        price: {
            percentage: 1,
            description: 'Du salaire brut annuel',
        },
    },
    {
        type: 'noGuarantee',
        name: "Je ne veux pas être garantie en cas de rupture de la période d'essai",
        warn:
            "En choisissant cette option, vous devez savoir que vous ne serez pas couvert en cas de  rupture de la période d'essai , qu'elle soit du fait du salarié ou de l'employeur.",
    },
];
type Props = {
    onChange: (value: any) => void;
    open: boolean;
    selected: string[];
    premiumOptionsRequired?: boolean;
    disabledOptions: string[];
    onClose: () => void;
};

type OptionItemProps = {
    data: PremiumOption;
    onChange?: (value: boolean, id: string) => void;
    onClick?: () => void;
    isSelected?: boolean;
    disabled?: boolean;
};

const StyledOptionButton = styled('button', {
    shouldForwardProp: prop => prop !== 'selectedState',
})<{ selectedState?: boolean }>(({ theme, selectedState }) => ({
    borderRadius: 16,
    borderWidth: 2.5,
    borderStyle: 'solid',
    borderColor: selectedState ? theme.palette.primary.main : theme.palette.border.main,
    backgroundColor: theme.palette.background.paper,
    padding: 16,
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    boxShadow: selectedState ? `0 4px 32px ${alpha(theme.palette.primary.main, 0.15)}` : 'none',
    '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
    },
}));

export const OptionItem = ({ data, onChange: onSelect, onClick, isSelected, disabled }: OptionItemProps) => {
    const { name, description, price, type: id } = data;
    const selected = useMemo(() => isSelected, [isSelected]);
    const isMobile = useMediaQuery('(max-width:600px)');

    return (
        <StyledOptionButton
            type="button"
            disabled={disabled}
            selectedState={Boolean(selected)}
            onClick={() => {
                onSelect && onSelect(!selected, id);
                onClick && onClick();
            }}>
            <Stack flexDirection={'row'} gap={2}>
                {onSelect && (
                    <i 
                        className={`fi ${selected ? 'fi-br-check' : 'fi-br-checkbox-blank'}`}
                        style={{ 
                            color: selected ? Colors.PRIMARY : Colors.NEUTRAL_DARK_GRAY,
                            fontSize: 24
                        }} 
                    />
                )}
                <Stack
                    flexDirection={{
                        xs: 'column',
                        md: 'row',
                    }}
                    flex={1}
                    gap={{
                        xs: 1,
                        sm: 2,
                    }}>
                    <Stack style={{ flex: 1 }}>
                        <Stack
                            flexDirection={{
                                xs: 'column',
                                md: 'row',
                            }}
                            gap={1}>
                            <Text medium heading dark>
                                {name}
                                {data.info && (
                                    <Popover hover>
                                        <TypographyRich>{data.info}</TypographyRich>
                                    </Popover>
                                )}
                            </Text>
                        </Stack>
                        {description && <Text dark>{description}</Text>}
                    </Stack>
                    {price && (
                        <Stack
                            flexDirection={'row'}
                            justifyContent={'flex-end'}
                            sx={{
                                gap: 1,
                                alignItems: 'center',
                            }}>
                            <Text medium size={18} primary>
                                {price.percentage}%
                            </Text>
                            <Text purple style={{ width: isMobile ? '100%' : 80 }}>
                                {price.description}
                            </Text>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </StyledOptionButton>
    );
};

/**
 * Mechanism: 
If option “ Je ne veux pas être garantie en cas de rupture de la période d'essai “ selected, the rest option will be uncheck

if option “ Garantie fin de période d'essai du fait du salarié valable les 60 premiers jours “ and “ Garantie fin de période d'essai du fait de l'employeur valable les 45 premiers jours” selected *note message won't be appear.
 * @param props 
 * @returns 
 */
const OptionItems = (props: Props) => {
    const isMobile = useMediaQuery('(max-width:600px)');
    const [selectedOptions, setSelectedOptions] = React.useState<any[]>(props.selected);
    const [termsChecked, setTermsChecked] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [showAlertPremium, setShowAlertPremium] = React.useState(false);

    const onClose = () => {
        if (!termsChecked) {
            setError(true);
            return;
        }
        if (props.onClose) {
            props.onClose();
        }
    };

    const onCancel = () => {
        if (props.onClose) {
            setError(false);
            props.onClose();
        }
    };

    const onTermsChecked = (value: boolean) => {
        setTermsChecked(value);
        if (!value) {
            if (selectedOptions.length > 1) {
                setError(true);
                return;
            }
        }
        setError(false);
    };

    const onChange = () => {
        if (!termsChecked && selectedOptions.length > 0) {
            setError(true);
            return;
        }
        setShowAlertPremium(true);
    };

    const onConfirm = () => {
        setShowAlertPremium(false);
        if (props.onChange) {
            props.onChange(selectedOptions);
        }
    };

    useEffect(() => {
        setSelectedOptions(props.selected);
        if (props.selected?.length > 0) {
            setTermsChecked(true);
        }
    }, [props.selected]);

    return (
        <>
            <Dialog
                open={showAlertPremium}
                onClose={() => {
                    setShowAlertPremium(false);
                }}
                PaperProps={{
                    style: {
                        borderColor: '#BDAE40',
                        borderWidth: 4,
                        borderStyle: 'solid',
                        borderRadius: 24,
                    },
                    sx: {
                        p: {
                            md: 4,
                            xs: 2,
                        },
                    },
                }}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    <IconButton
                        style={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            backgroundColor: Colors.NEUTRAL_LIGHT_GRAY,
                        }}
                        onClick={() => {
                            setShowAlertPremium(false);
                        }}>
                        <Icons name="close" size={24} color={Colors.NEUTRAL_BLACK} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Stack gap={2}>
                        <Text darkGray size={isMobile ? 14 : 16}>
                            Après avoir enregistrer votre offre Premium, vous pourrez toujours :
                        </Text>
                        <Stack gap={1}>
                            <Stack flexDirection={'row'} gap={2} alignItems="center">
                                <i className="fi-br-pencil" style={{ color: Colors.NEUTRAL_DARK_GRAY }} />
                                <Text dark medium size={isMobile ? 16 : 18}>
                                    Modifier le contenu de l'offre
                                </Text>
                            </Stack>
                            <Stack flexDirection={'row'} gap={2} alignItems="center">
                                <i className="fi-br-trash" style={{ color: Colors.NEUTRAL_DARK_GRAY }} />
                                <Text dark medium size={isMobile ? 16 : 18}>
                                    Supprimer l'offre et arrêter le recrutement*
                                </Text>
                            </Stack>
                        </Stack>
                        <Text darkGray size={isMobile ? 14 : 16}>
                            Vous ne pourrez plus :
                        </Text>
                        <Stack>
                            <Stack flexDirection={'row'} gap={2} alignItems="center">
                                <i className="fi fi-br-pen-slash" style={{ color: Colors.NEUTRAL_DARK_GRAY }} />
                                <Text dark medium size={isMobile ? 16 : 18}>
                                    Modifier le mode de recrutement “Premium”
                                </Text>
                            </Stack>
                        </Stack>
                        <div
                            style={{
                                alignSelf: 'center',
                            }}>
                            <Button
                                onClick={e => {
                                    onConfirm();
                                }}
                                variant="contained">
                                J'ai compris
                            </Button>
                        </div>
                        <Text darkGray size={isMobile ? 10 : 12}>
                            * En cas de suppression ou d'arrêt de l'offre Premium; tout candidat présenté par
                            l'intermédiaire d'Upercut qui serait recruté par l'entreprise, reste du à Upercut.
                        </Text>
                    </Stack>
                </DialogContent>
            </Dialog>

            <Dialog
                fullWidth
                maxWidth="md"
                open={props.open}
                onClose={onClose}
                fullScreen={isMobile}
               
                PaperProps={{
                    style: {
                        borderRadius: 16,
                        border: `4px solid ${Colors.PRIMARY_PURPLE_70} `,
                        boxShadow: 'none',
                        margin: isMobile ? 0 : 32,
                        width: isMobile ? '100%' : 'unset',
                        maxWidth: 'unset',
                        // borderRadius: 8,
                        position: 'relative',
                    },
                }}>
                <DialogContent>
                    <Stack
                        style={{
                            maxWidth: 772,
                            marginLeft: 'auto',
                            marginRight: 'auto',
                        }}>
                        <FormHeader
                            title="Garanties"
                            description="Sélectionnez les garanties que vous souhaitez ajouter à l'offre Premium"
                        />
                        <Alert variant="outlined" severity="info" sx={{ mb: 3 }}>
                            Option disponible uniquement dans le cadre de contrats à durée indéterminée ou CDD de plus
                            de 6 mois.
                        </Alert>
                        <Stack style={{ gap: 16 }}>
                            {permiumOptions.map((option, index) => (
                                <OptionItem
                                    key={index}
                                    data={option}
                                    isSelected={selectedOptions.includes(option.type)}
                                    disabled={props.disabledOptions.includes(option.type)}
                                    onChange={(value, id) => {
                                        setError(false);
                                        let _selectedOptions = selectedOptions;
                                        if (id === 'noGuarantee') {
                                            if (value) {
                                                setSelectedOptions([id]);
                                            } else {
                                                setSelectedOptions([]);
                                            }
                                            return;
                                        }
                                        _selectedOptions = selectedOptions.filter(Option => Option !== 'noGuarantee');

                                        if (value) {
                                            _selectedOptions = [..._selectedOptions, id];
                                        } else {
                                            _selectedOptions = _selectedOptions.filter(Option => Option !== id);
                                        }
                                        setSelectedOptions(_selectedOptions);
                                    }}
                                />
                            ))}
                            <Stack
                                flexDirection={'row'}
                                alignItems={'center'}
                                style={{
                                    marginLeft: 12,
                                }}>
                                <Checkbox
                                    id="terms"
                                    // type="checkbox"
                                    checked={termsChecked}
                                    onChange={e => onTermsChecked(e.target.checked)}
                                    name="terms"
                                    sx={{
                                        marginRight: 1,
                                        width: 36,
                                        height: 36,
                                    }}
                                />
                                <Typography
                                    component="label"
                                    htmlFor="terms"
                                    sx={{
                                        color: theme => (error ? theme.palette.error.main : theme.palette.text.primary),
                                        fontSize: 14,
                                        display: 'flex',
                                    }}>
                                    En cliquant sur ce bouton, vous déclarez avoir lu, compris et accepté
                                    <a href="/terms-and-conditions/option-premium" target="_blank">
                                        <span style={{ color: Colors.PRIMARY, fontWeight: 500 }}>
                                            {' '}
                                            les conditions générales d'utilisation{' '}
                                        </span>
                                    </a>{' '}
                                    et de vente de l'Option Premium.
                                </Typography>
                            </Stack>
                        </Stack>

                        <Stack
                            flexDirection={{
                                xs: 'column',
                                md: 'row',
                            }}
                            justifyContent={'space-between'}
                            sx={{
                                width: '100%',
                                marginTop: {
                                    xs: 2,
                                    md: 4,
                                },
                            }}
                            gap={{
                                xs: 2,
                                md: 0,
                            }}>
                            <Button
                                onClick={onCancel}
                                variant="text"
                                color="secondary"
                                >
                                Annuler
                            </Button>
                            <Stack
                                flexDirection={{
                                    xs: 'column',
                                    md: 'row',
                                }}
                                style={{ flex: 3 }}
                                gap={1}
                                alignItems={'center'}
                                justifyContent={'flex-end'}>
                                {selectedOptions.length === 1 && (
                                    <Stack
                                        flexDirection={{
                                            xs: 'column',
                                            md: 'row',
                                        }}
                                        style={{ flex: 1 }}
                                        gap={1}
                                        alignItems={'flex-start'}>
                                        <i className="fi fi-br-info" style={{ fontSize: 16, marginTop: 2, color: '#F2994A' }} />
                                        <Text dark style={{ flex: 1 }}>
                                            {permiumOptions.find(item => selectedOptions.includes(item.type)).warn}
                                        </Text>
                                    </Stack>
                                )}
                                <Button
                                    fullWidth={false}
                                    disabled={selectedOptions.length === 0 && props.premiumOptionsRequired}
                                    onClick={() => {
                                        if (selectedOptions.length === 0 && props.premiumOptionsRequired) {
                                            return;
                                        } else if (selectedOptions.length === 0) {
                                            onConfirm();
                                            props.onChange([]);
                                            return;
                                        }
                                        onChange();
                                    }}>
                                    Confirmer
                                </Button>
                            </Stack>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default OptionItems;
